import type { WireArticle, WireWorkflowStatus } from '../api/types'

type WorkflowCarrier = Pick<WireArticle, 'workflow'> | { workflow: WireWorkflowStatus }

export type WorkflowActionAccess = {
  stage: WireWorkflowStatus['stage']
  isBlocked: boolean
  isQcAssigned: boolean
  isLayoutAssigned: boolean
  isQcHolder: boolean
  isLayoutHolder: boolean
  isEditorialHolder: boolean
  isPostPubHolder: boolean
  isMyTurn: boolean
  isEditorialUser: boolean
  qcSigned: boolean
  layoutSigned: boolean
  editorialApproved: boolean
  canPassToLayout: boolean
  canReturnToQc: boolean
}

export type WorkflowUiAction =
  | 'qc_signoff'
  | 'pass_to_layout'
  | 'layout_signoff'
  | 'return_to_qc'
  | 'editorial_approve'
  | 'editorial_reject'
  | 'reschedule'
  | 'unschedule'
  | 'post_pub_finalize'
  | 'post_pub_revision'
  | 'post_pub_recall'

export function getWorkflowActionAccess(article: WorkflowCarrier, currentUserSub: string): WorkflowActionAccess {
  const workflow = article.workflow
  const stage = workflow.stage
  const cycle = workflow.cycles?.[workflow.currentCycle - 1]
  const isBlocked = Boolean(workflow.activeBlock)

  const isQcAssigned = workflow.qcAssignedTo === currentUserSub
  const isLayoutAssigned = workflow.layoutAssignedTo === currentUserSub
  const isQcHolder = stage === 'qc_review' && workflow.qcAssignedTo === currentUserSub
  const isLayoutHolder = stage === 'layout_review' && workflow.layoutAssignedTo === currentUserSub
  const isEditorialHolder = stage === 'editorial_approval' && workflow.editorialAssignedTo === currentUserSub
  const isPostPubHolder = stage === 'post_pub_review' && workflow.postPubAssignedTo === currentUserSub

  const qcSigned = Boolean(cycle?.qcSignoff)
  const layoutSigned = Boolean(cycle?.layoutSignoff)
  const editorialApproved = Boolean(cycle?.editorialApproval)

  return {
    stage,
    isBlocked,
    isQcAssigned,
    isLayoutAssigned,
    isQcHolder,
    isLayoutHolder,
    isEditorialHolder,
    isPostPubHolder,
    isMyTurn: isQcHolder || isLayoutHolder || isEditorialHolder,
    isEditorialUser: workflow.editorialAssignedTo === currentUserSub,
    qcSigned,
    layoutSigned,
    editorialApproved,
    // Both assignees may move between QC/Layout stages (push or pull).
    canPassToLayout: stage === 'qc_review' && (isQcAssigned || isLayoutAssigned),
    canReturnToQc: stage === 'layout_review' && (isQcAssigned || isLayoutAssigned),
  }
}

const ARTICLE_TOOLBAR_ACTIONS = new Set<WorkflowUiAction>([
  'qc_signoff',
  'pass_to_layout',
  'layout_signoff',
  'return_to_qc',
  'editorial_approve',
  'editorial_reject',
])

export function getVisibleWorkflowActions(article: WorkflowCarrier, currentUserSub: string): WorkflowUiAction[] {
  const access = getWorkflowActionAccess(article, currentUserSub)
  const actions: WorkflowUiAction[] = []

  const canPassToLayout = access.canPassToLayout
  const canReturnToQc = access.canReturnToQc

  if (access.isQcHolder) {
    if (!access.qcSigned) actions.push('qc_signoff')
  }

  if (access.isLayoutHolder) {
    if (!access.layoutSigned) actions.push('layout_signoff')
  }

  if (canPassToLayout) actions.push('pass_to_layout')
  if (canReturnToQc) actions.push('return_to_qc')

  if (access.isEditorialHolder && !access.editorialApproved) {
    actions.push('editorial_approve', 'editorial_reject')
  }

  if (access.stage === 'scheduled' && access.isEditorialUser) {
    actions.push('reschedule', 'unschedule')
  }

  if (access.stage === 'post_pub_review' && access.isPostPubHolder) {
    actions.push('post_pub_finalize', 'post_pub_revision', 'post_pub_recall')
  }

  return actions
}

export function getCardShelfWorkflowActions(article: WorkflowCarrier, currentUserSub: string): WorkflowUiAction[] {
  return getVisibleWorkflowActions(article, currentUserSub)
}

export function getArticleToolbarWorkflowActions(article: WorkflowCarrier, currentUserSub: string): WorkflowUiAction[] {
  return getVisibleWorkflowActions(article, currentUserSub).filter((action) => ARTICLE_TOOLBAR_ACTIONS.has(action))
}
